import { Injectable, inject, signal, computed, Signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, shareReplay, tap, finalize, catchError, of, switchMap } from 'rxjs';
import { AuthUser, LoginResponse, RefreshResponse, Role } from '../models';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private get baseUrl() { return `${environment.apiUrl}auth`; }

  private readonly accessToken = signal<string | null>(null);
  private readonly _currentUser = signal<AuthUser | null>(null);

  readonly currentUser: Signal<AuthUser | null> = this._currentUser.asReadonly();
  readonly roles = computed<Role[]>(() => this._currentUser()?.roles ?? []);
  readonly isAuthenticated = computed(() => this._currentUser() !== null);

  // Coalescing: mehrere parallele 401s dürfen nur EINEN Netzwerk-Refresh auslösen.
  private refreshInFlight: Observable<RefreshResponse> | null = null;

  hasRole(role: Role): boolean {
    return this.roles().includes(role);
  }

  hasAnyRole(allowed: Role[]): boolean {
    return this.roles().some(r => allowed.includes(r));
  }

  /** Für den Interceptor: synchroner Zugriff auf den aktuellen Access-Token. */
  accessTokenValue(): string | null {
    return this.accessToken();
  }

  login(username: string, password: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${this.baseUrl}/login`, { username, password }, { withCredentials: true })
      .pipe(
        tap(res => {
          this.accessToken.set(res.accessToken);
          this._currentUser.set(res.user);
        })
      );
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/logout`, {}, { withCredentials: true }).pipe(
      catchError(() => of(void 0)),
      tap(() => this.clearSession())
    );
  }

  refresh(): Observable<RefreshResponse> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    const request$ = this.http
      .post<RefreshResponse>(`${this.baseUrl}/refresh`, {}, { withCredentials: true })
      .pipe(
        tap(res => this.accessToken.set(res.accessToken)),
        shareReplay(1),
        finalize(() => { this.refreshInFlight = null; })
      );

    this.refreshInFlight = request$;
    return request$;
  }

  /**
   * Session nach vollständigem Seitenreload wiederherstellen (Access-Token ist
   * reiner Speicher-State und geht bei F5 verloren, siehe ADR 0003). Wird
   * einmalig beim App-Start über provideAppInitializer aufgerufen und schluckt
   * Fehler (kein gültiger Refresh-Cookie → einfach ausgeloggt bleiben).
   */
  tryRestoreSession(): Observable<void> {
    return this.refresh().pipe(
      switchMap(() => this.http.get<AuthUser>(`${this.baseUrl}/me`)),
      tap(user => this._currentUser.set(user)),
      catchError(() => {
        this.clearSession();
        return of(null);
      }),
      switchMap(() => of(void 0))
    );
  }

  private clearSession(): void {
    this.accessToken.set(null);
    this._currentUser.set(null);
  }
}
